import { z } from 'zod';

import { emailSchema } from './auth.ts';

/**
 * Contratto dei fantallenatori e dei magic link.
 *
 * Il fantallenatore non ha credenziali: riceve un link firmato via email e da
 * quello nasce una sessione valida **per una sola asta**. Tutto ciò che serve a
 * descrivere quel flusso — rotte, limiti, stati dell'invito — vive qui.
 */
export const PARTICIPANT_ROUTES = {
  /** Template Fastify; per un URL concreto usa `auctionParticipantsPath()`. */
  collection: '/auctions/:id/participants',
  byId: '/auctions/:id/participants/:participantId',
  invitations: '/auctions/:id/participants/:participantId/invitations',
  /**
   * Magic link restituito all'amministratore invece che spedito.
   *
   * Serve quando il recapito non è l'email — WhatsApp, Telegram, a voce. È
   * l'**unica** risposta dell'API che contiene un token di invito in chiaro:
   * il link non è recuperabile in un secondo momento, perché nel database ne
   * esiste solo l'hash.
   */
  invitationLink: '/auctions/:id/participants/:participantId/invitations/link',
  /**
   * Sessione fantallenatore aperta dall'amministratore, per provare l'asta dal
   * lato di chi offre. Non emette né consuma inviti: apre una sessione e basta.
   */
  impersonate: '/auctions/:id/participants/:participantId/impersonate',
} as const;

export function auctionParticipantsPath(auctionId: string): string {
  return `/auctions/${encodeURIComponent(auctionId)}/participants`;
}

export function auctionParticipantPath(auctionId: string, participantId: string): string {
  return `${auctionParticipantsPath(auctionId)}/${encodeURIComponent(participantId)}`;
}

export function participantInvitationsPath(auctionId: string, participantId: string): string {
  return `${auctionParticipantPath(auctionId, participantId)}/invitations`;
}

export function participantInvitationLinkPath(auctionId: string, participantId: string): string {
  return `${participantInvitationsPath(auctionId, participantId)}/link`;
}

export function participantImpersonatePath(auctionId: string, participantId: string): string {
  return `${auctionParticipantPath(auctionId, participantId)}/impersonate`;
}

/**
 * Rotte del fantallenatore.
 *
 * Il token **non** compare nell'URL di queste chiamate: viaggia nel corpo di
 * `redeem`. Un token in un path finirebbe nei log di accesso, nella cronologia
 * e nell'header `Referer` di ogni risorsa caricata dalla pagina.
 */
export const INVITE_ROUTES = {
  redeem: '/invite/redeem',
  me: '/participant/me',
  logout: '/participant/logout',
} as const;

/**
 * Percorso del magic link nel client, come richiesto dal capitolato:
 * `https://dominio/invite/{token}`. La pagina legge il token dall'URL e lo
 * scambia con `POST /invite/redeem`.
 */
export function inviteLinkPath(token: string): string {
  return `/invite/${token}`;
}

// ---------------------------------------------------------------------------
// Enum condivisi
// ---------------------------------------------------------------------------

export const PARTICIPANT_STATUSES = ['INVITED', 'ACTIVE', 'REMOVED'] as const;

export type ParticipantStatusValue = (typeof PARTICIPANT_STATUSES)[number];

export const participantStatusSchema = z.enum(PARTICIPANT_STATUSES);

// ---------------------------------------------------------------------------
// Limiti
// ---------------------------------------------------------------------------

export const PARTICIPANT_NAME_MIN_LENGTH = 2;
export const PARTICIPANT_NAME_MAX_LENGTH = 80;

/** Durata di un invito. Default una settimana: un'asta si organizza per tempo. */
export const INVITE_TTL_HOURS_DEFAULT = 168;
export const INVITE_TTL_HOURS_LIMITS = { min: 1, max: 24 * 90 } as const;

/**
 * Budget personalizzato per singolo partecipante.
 *
 * Se assente eredita `AuctionSettings.initialBudget`. Il campo esiste dal
 * task 02 proprio perché questa personalizzazione fosse possibile senza
 * migrazione.
 */
export const PARTICIPANT_BUDGET_LIMITS = { min: 1, max: 100_000 } as const;

export const participantNameSchema = z
  .string()
  .trim()
  .min(PARTICIPANT_NAME_MIN_LENGTH, 'Nome troppo corto')
  .max(
    PARTICIPANT_NAME_MAX_LENGTH,
    `Il nome non può superare ${PARTICIPANT_NAME_MAX_LENGTH} caratteri`,
  );

const participantBudgetSchema = z
  .number()
  .int('Deve essere un numero intero di fantamilioni')
  .min(PARTICIPANT_BUDGET_LIMITS.min, 'Il budget deve essere positivo')
  .max(
    PARTICIPANT_BUDGET_LIMITS.max,
    `Non può superare ${PARTICIPANT_BUDGET_LIMITS.max} fantamilioni`,
  );

// ---------------------------------------------------------------------------
// Richieste
// ---------------------------------------------------------------------------

export const createParticipantRequestSchema = z.object({
  displayName: participantNameSchema,
  email: emailSchema,
  /** Omesso: eredita il budget di default dell'asta. */
  initialBudget: participantBudgetSchema.optional(),
});

export type CreateParticipantRequest = z.input<typeof createParticipantRequestSchema>;

/** Come per le aste, un PATCH vuoto è un errore, non un no-op silenzioso. */
export const updateParticipantRequestSchema = z
  .object({
    displayName: participantNameSchema.optional(),
    initialBudget: participantBudgetSchema.optional(),
  })
  .refine((value) => value.displayName !== undefined || value.initialBudget !== undefined, {
    message: 'Nessuna modifica richiesta',
  });

export type UpdateParticipantRequest = z.input<typeof updateParticipantRequestSchema>;

export const participantIdParamsSchema = z.object({
  id: z.uuid('Identificativo asta non valido'),
  participantId: z.uuid('Identificativo partecipante non valido'),
});

/**
 * Il token è validato per forma prima di toccare il database: 43 caratteri
 * base64url, cioè 32 byte. Una stringa di forma diversa non può essere nostra.
 */
export const INVITE_TOKEN_LENGTH = 43;

export const redeemInviteRequestSchema = z.object({
  token: z
    .string()
    .trim()
    .length(INVITE_TOKEN_LENGTH, 'Link di invito non valido')
    .regex(/^[A-Za-z0-9_-]+$/, 'Link di invito non valido'),
});

export type RedeemInviteRequest = z.input<typeof redeemInviteRequestSchema>;

// ---------------------------------------------------------------------------
// Risposte
// ---------------------------------------------------------------------------

/** Stato dell'invito più recente di un partecipante, per la dashboard. */
export interface InvitationSummary {
  readonly id: string;
  /** ISO-8601. */
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly revokedAt: string | null;
  /** Prima volta che il link è stato usato. */
  readonly consumedAt: string | null;
  /** Calcolato dal server: il client non deve dedurlo dalle date. */
  readonly active: boolean;
}

export interface ParticipantSummary {
  readonly id: string;
  readonly auctionId: string;
  readonly displayName: string;
  readonly email: string;
  readonly status: ParticipantStatusValue;
  readonly initialBudget: number;
  readonly spentBudget: number;
  readonly remainingBudget: number;
  readonly createdAt: string;
  /** `null` se il partecipante non è mai stato invitato. */
  readonly invitation: InvitationSummary | null;
}

export interface ParticipantResponse {
  readonly participant: ParticipantSummary;
}

export interface ParticipantListResponse {
  readonly participants: readonly ParticipantSummary[];
  readonly total: number;
}

export interface ParticipantRemovedResponse {
  readonly removed: true;
  /** Quanti inviti ancora aperti sono stati revocati insieme al partecipante. */
  readonly revokedInvitations: number;
}

export interface InvitationRevokedResponse {
  readonly revokedInvitations: number;
}

/**
 * Magic link consegnato all'amministratore perché lo recapiti lui.
 *
 * `inviteUrl` contiene il token in chiaro e **non è ripetibile**: nel database
 * ne resta solo l'hash, quindi chiedere di nuovo il link significa emetterne
 * uno nuovo — e quello precedente smette di funzionare.
 */
export interface InvitationLinkResponse {
  readonly participant: ParticipantSummary;
  readonly inviteUrl: string;
  /** Scadenza del link, ISO-8601: è quella che l'amministratore comunica. */
  readonly expiresAt: string;
}

/**
 * Profilo del fantallenatore autenticato.
 *
 * Non contiene mai il token: la sessione vive nel cookie HTTP-only.
 */
export interface ParticipantProfile {
  readonly id: string;
  readonly displayName: string;
  readonly auctionId: string;
  readonly auctionName: string;
  readonly initialBudget: number;
  readonly spentBudget: number;
  readonly remainingBudget: number;
}

export interface ParticipantSessionResponse {
  readonly participant: ParticipantProfile;
  /** Scadenza della sessione, ISO-8601. */
  readonly expiresAt: string;
}

export interface ParticipantLogoutResponse {
  readonly loggedOut: true;
}
